import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@rossum/ui/material';
import { createStoreHooks } from '../../../../../canvas/core';
import type { TranslationViewModel } from './useTranslationsViewModels';

const { useStore } = createStoreHooks();

type ActiveTranslationOverlayProps = {
  activeViewModel: TranslationViewModel | null;
};

const ACTIVE_PADDING_PX = 6;
const ACTIVE_GAP_PX = 8;

export const ActiveTranslationOverlay = ({
  activeViewModel,
}: ActiveTranslationOverlayProps) => {
  const theme = useTheme();
  const zoom = useStore(s => s.canvas.transform.zoom);
  const [vm, setVm] = useState<TranslationViewModel | null>(null);
  const [isActive, setIsActive] = useState(false);
  const exitTimeoutRef = useRef<number>();
  const activateTimeoutRef = useRef<number>();

  useEffect(() => {
    if (activeViewModel) {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = undefined;
      }

      if (!vm) {
        setVm(activeViewModel);
        setIsActive(false);
        activateTimeoutRef.current = window.setTimeout(() => {
          setIsActive(true);
        }, 16);
      } else {
        setVm(activeViewModel);
        setIsActive(true);
      }
    } else if (vm) {
      setIsActive(false);
      exitTimeoutRef.current = window.setTimeout(() => {
        setVm(null);
      }, 160);
    }

    return () => {
      if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
      if (activateTimeoutRef.current) clearTimeout(activateTimeoutRef.current);
    };
  }, [activeViewModel, vm]);

  if (!vm) return null;

  const zoomInverse = 1 / zoom;
  const padding = isActive ? ACTIVE_PADDING_PX * zoomInverse : 0;
  const lift = isActive ? vm.height + ACTIVE_GAP_PX * zoomInverse : 0;

  const x = isActive ? vm.x - padding : vm.x;
  const y = isActive ? vm.y - padding : vm.y;
  const width = isActive ? vm.width + 2 * padding : vm.width;
  const height = isActive ? vm.height + 2 * padding : vm.height;
  const textY = (height - vm.fontSize) / 2;

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        height,
        paddingTop: textY,
        paddingLeft: isActive ? padding : 0,
        paddingRight: isActive ? padding : 0,
        backgroundColor: isActive ? '#333' : 'white',
        color: isActive ? 'white' : 'black',
        borderRadius: isActive ? 4 : 0,
        boxShadow: isActive ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none',
        transform: `translateY(${isActive ? -lift : 0}px)`,
        transition: 'all 150ms ease',
        fontSize: vm.fontSize,
        fontFamily: theme.typography.fontFamily,
        fontWeight: 400,
        lineHeight: `${vm.fontSize}px`,
        whiteSpace: 'nowrap',
        overflow: 'visible',
        pointerEvents: 'none',
        userSelect: 'none',
        boxSizing: 'border-box',
      }}
      title={vm.text}
    >
      {vm.text}
    </div>
  );
};
